/**
 * ResultsPage Component
 */

import * as playerAction from '../../../../redux/actions/player';
import { getPlayerTracksFromResults } from '../../../../redux/helpers/player';
import { trackSearchFragment, trackSearchQuery } from '../../../../relay/trackSearch';
import './styles.scss';

import * as classNames from 'classnames';
import { RouteMatch } from 'found';
import { get, isEqualWith } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createRefetchContainer } from 'react-relay';

import Page from '../../../../containers/Page';
import { CurrencyContext } from '../../../../contexts/currency';
import { IQueryStringContext, QueryStringContext } from '../../../../contexts/queryString';
import { hasSearchFilters } from '../../../../contexts/queryString/selectors';
import LoadingSpinner from '../../../atoms/feedback/LoadingSpinner';
import NProgress from '../../../atoms/feedback/NProgress';
import FacetedSearchNav from '../../../organisms/search/FacetedSearchNav';
import ResultList from '../../../organisms/search/ResultList';
import EmptyState from '../../../organisms/sections/EmptyState';
import { NoMatchFound } from './NoMatchFound';

// Page Props
interface IOwnDispatchProps {
  setPlayerQueue: (tracks: IPlayerTrack[]) => void;
}

interface IOwnContextProps extends Pick<IQueryStringContext,
  'filters' | 'license_params' | 'query' | 'taxonomy_query' | 'sort'
> {
  hasFilters: boolean;
}

interface IOwnProps {
  searching?: boolean;
  relay?: IRelayProps<ITrackSearchQueryVariables>;
  viewer: GQL.IViewer | null;
}

type ResultsPageProps =
  & IOwnProps
  & IOwnDispatchProps
  & IOwnContextProps
  & RouteMatch;

export type IResultListExposedProps = Optional<RouteMatch> & Pick<IOwnProps,
  | 'relay'
  | 'searching'
  | 'viewer'>;

interface IOwnState {
  displayPage: number;
  prevResults: GQL.ITrackSearchResultEdge[];
  hasFacetMenuOpen: boolean;
  searching: boolean;
}

export const ResultsPerPage = 15;
const classes = new BEM('results');
const defaultList = [];

class ResultsPage extends React.Component<ResultsPageProps, IOwnState> {
  static getDerivedStateFromProps({ searching }: ResultsPageProps, prevState: IOwnState) {
    if (searching !== prevState.searching) {
      return {
        searching,
      };
    }

    return null;
  }

  private searchTimer: number;

  constructor(props) {
    super(props);
    this.state = {
      prevResults: [],
      displayPage: 1,
      hasFacetMenuOpen: false,
      searching: !!this.props.searching,
    };
  }

  componentDidMount() {
    const tracks = getPlayerTracksFromResults(getTrackResultsFromEdges(this.props.viewer));
    this.props.setPlayerQueue(tracks);
  }

  shouldComponentUpdate(nextProps: ResultsPageProps, nextState: IOwnState) {
    // FIXME: this line is not clear at all...
    return nextState.displayPage <= this.state.displayPage && this.filtersWereTurnedOn(nextProps) === false;
  }

  componentDidUpdate(prevProps: ResultsPageProps) {
    if (!this.props.searching) {
      if (this.hasNewResults(prevProps)) {
        const newTracks = getPlayerTracksFromResults(getTrackResultsFromEdges(this.props.viewer));
        this.props.setPlayerQueue(newTracks);

        if (this.isNewSearch(prevProps)) {
          this.setState({ displayPage: 1, prevResults: [] });
        }
      }
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.searchTimer);
  }

  onLoadMore = () => {
    this.setState(({ displayPage, prevResults }) => {
      const newDisplayPage = displayPage + 1;

      const {
        filters,
        license_params,
        relay,
        query,
        taxonomy_query,
        sort,
      } = this.props;

      const refetchVariables: ITrackSearchQueryVariables = {
        offset: ResultsPerPage * (newDisplayPage - 1),
        count: ResultsPerPage,
        filters,
        license_params,
        taxonomy_query,
        sort,
        query,
      };

      relay!.refetch(refetchVariables, null, null, { force: true });

      return {
        prevResults: [...prevResults, ...this.props.viewer!.trackSearch!.edges!],
        displayPage: newDisplayPage,
        searching: true,
      };
    });
  }

  onToggleFacetMenu = (open?: boolean) => {
    if (this.state.hasFacetMenuOpen !== open) {
      this.setState({ hasFacetMenuOpen: !!open });
    }
  }

  render() {
    const { hasFilters, viewer } = this.props;
    const { hasFacetMenuOpen, searching } = this.state;

    const results = viewer ? [...this.state.prevResults, ...viewer.trackSearch!.edges!] : defaultList;
    const totalResults = viewer ? viewer.trackSearch!.total! : 0;

    const contentClassModifiers = {
      'no-result': hasFilters && totalResults === 0,
    };

    const extraClasses = classNames(
      hasFacetMenuOpen && 'has-facet-menu-open',
      searching && 'searching',
      !hasFilters && 'is-empty-state',
    );

    return (
      <Page id="results" className={extraClasses}>
        <CurrencyContext.Consumer>
          {({ currency }) =>
            <FacetedSearchNav
              onToggleFacetMenu={this.onToggleFacetMenu}
              viewer={viewer}
              currency={currency}
            />
          }
        </CurrencyContext.Consumer>
        <NProgress active={!!searching} />
        <div {...classes('content-wrapper')}>
          <div {...classes('content', contentClassModifiers)}>
            {hasFilters ?
              totalResults > 0 ?
                <ResultList results={results} totalResults={totalResults} onLoadMore={this.onLoadMore} />
                  : searching ? <LoadingSpinner center /> : <NoMatchFound />
              : <EmptyState />
            }
          </div>
        </div>
      </Page>
    );
  }

  private isNewSearch(prevProps: ResultsPageProps) {
    const [prevResults, newResults] = this.getResults(prevProps);
    return prevResults.length === 0 && newResults.length > 0;
  }

  private hasNewResults(prevProps: ResultsPageProps) {
    const [prevResults, newResults] = this.getResults(prevProps);

    if (prevResults.length !== newResults.length) {
      return true;
    }

    return !isEqualWith(prevResults, newResults,
      (a: GQL.ITrackSearchResultEdge[], b: GQL.ITrackSearchResultEdge[]) => {
        for (const [i] of a.entries()) {
          if (a[i].node && b[i].node) {
            if (a[i].node!.id !== b[i].node!.id) {
              return false;
            }
          } else if (!!a[i].node && b[i].node || a[i].node && !!b[i].node) {
            return false;
          }
        }
        return true;
      });
  }

  private getResults(prevProps: ResultsPageProps) {
    const prevResults: GQL.ITrackSearchResultEdge[] = get(prevProps, 'viewer.trackSearch.edges', defaultList);
    const newResults: GQL.ITrackSearchResultEdge[] = get(this.props, 'viewer.trackSearch.edges', defaultList);
    return [ prevResults, newResults ];
  }

  private filtersWereTurnedOn(nextProps: ResultsPageProps) {
    return !this.props.hasFilters && nextProps.hasFilters;
  }
}

function getTrackResultsFromEdges(viewer: GQL.IViewer | null) {
  if (!!!viewer || !!!viewer.trackSearch) {
    return [];
  }
  return viewer.trackSearch.edges!.map(({ node }) => node!);
}

const ConnectedResultsPage =
  connect<{}, IOwnDispatchProps, IOwnProps & IOwnContextProps, IStoreState>(
    null,
    (dispatch) => ({
      setPlayerQueue: (tracks: IPlayerTrack[]) => {
        dispatch(playerAction.replaceQueue({ tracks }));
      },
    }),
  )(ResultsPage);

const ResultPageWithQueryStringContext: React.SFC<IOwnProps & RouteMatch> = (props) => (
  <QueryStringContext.Consumer>
    {(ctx) => {
      const hasFilters = hasSearchFilters(ctx);
      return (
        <ConnectedResultsPage
          filters={ctx.filters}
          hasFilters={hasFilters}
          license_params={ctx.license_params}
          query={ctx.query}
          sort={ctx.sort}
          {...props}
        />
      );
    }}
  </QueryStringContext.Consumer>
);

const RelayResultsPage = createRefetchContainer(
  ResultPageWithQueryStringContext,
  trackSearchFragment,
  trackSearchQuery,
);

export default RelayResultsPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/browse/Results/index.tsx